import {
  ParsedMetafields,
  flattenConnection,
  parseMetafield,
} from "@shopify/hydrogen-react";
import type {
  Product,
  MediaConnection,
  MetaobjectConnection,
  Metaobject,
} from "@shopify/hydrogen-react/storefront-api-types";
import { useQuery } from "react-query";
import { api } from "utils/api";
import { sanitizeShopifyHTML } from "../components/ShopifyRichText";
import { parseGid } from "../utils/shopifyId";
import { productAdminKeys } from "./queryKeys";
import type {
  MetaFieldImageData,
  ProductAdminData,
  ProductStatus,
  VariantAdminNode,
} from "../types";

type ProductAdminResp = Pick<
  Product,
  | "id"
  | "description"
  | "descriptionHtml"
  | "handle"
  | "productType"
  | "tags"
  | "title"
  | "updatedAt"
  | "vendor"
  | "featuredImage"
  | "options"
> & {
  artistId?: { value: string };
  publishedAt?: { value: string };
  status?: { value: ProductStatus };
  templateId?: { value: string };
  assetUrl?: { value: string };
  maximumQuantity?: { value: string };
  onChainJSON?: { value: string };
  imageData?: { value: string };
  details?: {
    value: string;
    references: Partial<MetaobjectConnection>;
  };
  variants: {
    nodes: VariantAdminNode[];
  };
  media: MediaConnection;
};

const getProductAdmin = (id?: string | null) =>
  id
    ? api.get<{ product?: ProductAdminResp }>(`/shop/product/${id}`)
    : Promise.reject(new Error("Invalid product id"));

// the shape of data stored in the metafield
type OnChainJSON = {
  contractId: string;
  tokenName: string;
  tokenDescription: string;
  royalty: number;
};

/**
 * Fetch Product by id for managing as shop admin
 */
const useProductAdminQuery = (id?: string | null) =>
  useQuery<
    { product?: ProductAdminResp },
    unknown,
    ProductAdminData | undefined
  >(productAdminKeys.detail(id), () => getProductAdmin(id), {
    enabled: !!id,
    select: ({ product }) => {
      if (!product) {
        return undefined;
      }

      // Flatten all connections from metafields or other relationships
      const details = !product.details?.references
        ? []
        : flattenConnection(product.details.references).filter(
            (d): d is Metaobject => !!d,
          );
      const media = !product.media ? [] : flattenConnection(product.media);
      const variants = !product.variants
        ? []
        : flattenConnection(product.variants).map((v) => ({
            ...v,
            templateVariantId: v.templateVariantId?.value,
          }));

      // Parse JSON Metafields
      const imageData =
        parseMetafield<ParsedMetafields<MetaFieldImageData>["json"]>(
          product.imageData ?? { type: "json", value: "{}" },
        ).parsedValue || undefined;
      const onChainJSON = parseMetafield<ParsedMetafields<OnChainJSON>["json"]>(
        product.onChainJSON ?? { type: "json", value: "{}" },
      ).parsedValue;

      return {
        ...product,
        id: parseGid(product.id).id,
        gid: product.id,
        artistId: product.artistId?.value,
        assetUrl: product.assetUrl?.value,
        descriptionHtml: sanitizeShopifyHTML(product.descriptionHtml),
        details,
        imageData,
        maximumQuantity: product.maximumQuantity?.value,
        media,
        ...onChainJSON,
        publishedAt: product.publishedAt?.value,
        status: product.status?.value,
        templateId: product.templateId?.value,
        variants,
      };
    },
  });

export { useProductAdminQuery, type MetaFieldImageData };
